/**
 * Loyalty portal primitives — every colour, logo and wallpaper comes from the
 * organization's own saved design (loyalty_settings.design), resolved by the
 * server. Nothing here knows an industry.
 *
 * THIS FILE IS THE ONLY COPY. pages/StaffMenu.jsx is deliberately NOT touched
 * and NOT imported: switching staff onto shared primitives is a separate,
 * separately approved job.
 */
import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { fontStack, loadFont } from '../../lib/designTokens';
import { TYPE_SCOPE, bodyStyle, buttonStyle, chosenFonts, headlineStyle,
    portalCss } from './typography';

const API_BASE = (typeof window !== 'undefined' ? window.location.origin : '');
export const absUrl = (u) => (!u ? null : (String(u).startsWith('http') ? u : `${API_BASE}${u}`));

export const Screen = ({ children, testid, branding = {}, theme = {}, framed = false }) => {
    const wallpaper = absUrl(branding.wallpaper_url);
    const zoom = Math.max(100, Math.min(200, Number(branding.wallpaper_zoom) || 100));
    const posX = Number.isFinite(Number(branding.wallpaper_x)) ? Number(branding.wallpaper_x) : 50;
    const posY = Number.isFinite(Number(branding.wallpaper_y)) ? Number(branding.wallpaper_y) : 50;
    // THE WALLPAPER MUST COVER THE WHOLE PAGE, HOWEVER LONG IT GETS.
    // In the real portal the layers are pinned to the screen and the content
    // scrolls over them: no stretching on a long list, no plain band at the
    // bottom, and nothing to measure wrongly. The framed designer preview keeps
    // its old absolute layers inside the phone shell, untouched.
    const pin = framed ? 'absolute' : 'fixed';
    // The four text styles. With nothing saved every one of these is undefined
    // and `css` is empty, so the portal renders exactly as it did before.
    const body = bodyStyle(theme);
    const css = portalCss(theme);
    // The chosen faces are self-hosted and fetched from our own domain only,
    // one stylesheet per family, exactly like the gift setups do it.
    const fonts = chosenFonts(theme).join(',');
    useEffect(() => { fonts.split(',').filter(Boolean).forEach(loadFont); }, [fonts]);
    return (
        <div className={`${framed ? 'relative min-h-full' : 'relative min-h-screen'} w-full ${framed ? 'overflow-hidden' : ''}`}
             style={{ backgroundColor: theme.bg || '#020617', color: theme.text || '#ffffff',
                      fontFamily: fontStack(theme.body_font) || undefined,
                      ...body }}
             {...{ [`data-${TYPE_SCOPE}`]: 'on' }}
             data-testid={testid}>
            {css ? <style data-testid="loyalty-type-css">{css}</style> : null}
            {wallpaper && (
                <>
                    <div className={`${pin} inset-0 overflow-hidden`} aria-hidden="true"
                         style={framed ? undefined : { minHeight: '100dvh' }}>
                        <div className="absolute inset-0 bg-no-repeat"
                             style={{ backgroundImage: `url(${wallpaper})`,
                                      backgroundSize: branding.wallpaper_fit === 'whole' ? 'contain' : 'cover',
                                      backgroundPosition: `${posX}% ${posY}%`,
                                      transform: zoom === 100 ? undefined : `scale(${zoom / 100})`,
                                      transformOrigin: `${posX}% ${posY}%` }}
                             data-testid="loyalty-wallpaper" data-fit={branding.wallpaper_fit || 'fill'}
                             data-zoom={zoom} />
                    </div>
                    <div className={`${pin} inset-0`} aria-hidden="true"
                         style={{ backgroundColor: `rgba(0,0,0,${(Number(branding.wallpaper_dark) || 0) / 100})`,
                                  ...(framed ? {} : { minHeight: '100dvh' }) }}
                         data-testid="loyalty-wallpaper-dark" />
                </>
            )}
            <div className="relative mx-auto w-full max-w-sm space-y-6 px-5 py-8">{children}</div>
        </div>
    );
};

export const Logos = ({ branding = {} }) => (
    <div className="space-y-3 text-center">
        {branding.merchant_logo_url && (
            <img src={absUrl(branding.merchant_logo_url)} alt="" data-testid="loyalty-merchant-logo"
                 className="mx-auto object-contain"
                 style={{ maxWidth: '100%', height: `${Number(branding.merchant_logo_px) || 96}px` }} />
        )}
        {branding.program_logo_url && (
            <img src={absUrl(branding.program_logo_url)} alt="" data-testid="loyalty-program-logo"
                 className="mx-auto object-contain"
                 style={{ maxWidth: '100%', height: `${Number(branding.program_logo_px) || 72}px` }} />
        )}
    </div>
);

export const Title = ({ children, theme = {}, testid }) => {
    const style = headlineStyle(theme);
    return (
        // A chosen alignment replaces the centring; with none chosen it stays centred.
        <h1 className={`font-bold leading-tight ${style.textAlign ? '' : 'text-center'}`}
            data-testid={testid} style={style}>
            {children}
        </h1>
    );
};

/** A touch lighter than the colour itself — the top of the metal sheen. */
const lighten = (hex, amount = 0.16) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!m) return null;
    const n = parseInt(m[1], 16);
    const mix = (c) => Math.round(c + (255 - c) * amount);
    return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
};

/** And a touch deeper — the shaded belly of it. */
const darken = (hex, amount = 0.18) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!m) return null;
    const n = parseInt(m[1], 16);
    const mix = (c) => Math.round(c * (1 - amount));
    return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
};

export const BigButton = ({ children, onClick, testid, tone = 'primary', disabled,
                           preview = false, theme = {}, bg, fg, icon: Icon, lux = false,
                           light = null }) => {
    // A COLOUR CHOSEN IN THE DESIGNER ALWAYS WINS, on both tones. Only when the
    // slot has no colour of its own does the old look come back: a quiet button
    // stays a translucent pill, a primary one takes the program colours.
    const quiet = tone === 'quiet';
    const style = {
        background: bg || (quiet ? 'rgba(255,255,255,.08)' : (theme.button || '#f5a623')),
        color: fg || (quiet ? (theme.text || '#e2e8f0') : (theme.button_text || '#111827')),
        ...buttonStyle(theme),
    };
    // METAL AND GLASS, only for a clinic that switched them on. A button with
    // its own colour gets a soft top-to-bottom sheen of that same colour; a
    // button with no colour of its own becomes glass over the wallpaper with a
    // hairline border in the program's accent.
    if (lux || theme.lux_buttons) {
        const top = lighten(bg, 0.40);
        const mid = lighten(bg, 0.06);
        const deep = darken(bg, 0.22);
        if (bg && top) {
            // BRUSHED METAL: light champagne at the top, a deeper band through
            // the middle, and a thin highlight catching the bottom edge.
            style.background = `linear-gradient(180deg, ${top} 0%, ${mid} 20%, ${bg} 48%,`
                + ` ${deep} 84%, ${lighten(bg, 0.26)} 100%)`;
            style.boxShadow = 'inset 0 1px 0 rgba(255,255,255,0.55),'
                + ' inset 0 -1px 0 rgba(0,0,0,0.28), 0 6px 18px rgba(0,0,0,0.38)';
            style.border = '1px solid rgba(0,0,0,0.18)';
        } else if (!bg) {
            // GLASS: darker fill so the wallpaper reads through it, soft blur,
            // a hairline of the program's accent and an inner top highlight.
            const edge = theme.accent || '#C5A059';
            style.background = 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%,'
                + ' rgba(10,7,3,0.34) 100%)';
            style.border = `1px solid ${edge}b3`;
            style.color = fg || '#ffffff';
            style.backdropFilter = 'blur(10px)';
            style.WebkitBackdropFilter = 'blur(10px)';
            style.boxShadow = 'inset 0 1px 0 rgba(255,255,255,0.20),'
                + ' 0 4px 14px rgba(0,0,0,0.30)';
        }
    }
    return (
        // In the designer the buttons are shown at full strength and simply cannot be
        // pressed; on a member's phone an unfinished form still greys the button out.
        <button type="button" onClick={onClick} data-testid={testid}
                disabled={preview ? false : disabled}
                style={preview ? { ...style, pointerEvents: 'none' } : style}
                className="flex w-full items-center justify-center gap-4 rounded-2xl px-6 py-5
                           font-semibold transition-transform duration-150 active:scale-[0.98]
                           disabled:opacity-50">
            {Icon ? <Icon className="h-5 w-5 shrink-0" strokeWidth={1.5}
                         aria-hidden="true" data-testid={testid ? `${testid}-icon` : undefined} />
                : null}
            <span className="truncate">{children}</span>
            {light}
        </button>
    );
};

/** A SMALL LIGHT at the right end of a menu button: red with a number when
 * something needs a manager, green with a tick when nothing does. */
export const Light = ({ count = 0, testid }) => {
    const waiting = Number(count) > 0;
    const colour = waiting ? '#dc2626' : '#16a34a';
    return (
        <span data-testid={testid} data-count={String(count)}
              className="ml-auto flex h-3 w-3 shrink-0 items-center justify-center
                         rounded-full text-[8px] font-bold leading-none text-white"
              style={{ background: colour, boxShadow: `0 0 8px 2px ${colour}66` }}>
            {waiting ? count : '✓'}
        </span>
    );
};

export const BackBar = ({ onBack, label, testid = 'loyalty-back' }) => (
    <button type="button" onClick={onBack} data-testid={testid}
            className="flex items-center gap-2 text-sm opacity-70 hover:opacity-100">
        <ArrowLeft className="h-4 w-4" /> {label}
    </button>
);

export const Field = ({ label, testid, theme, ...rest }) => (
    <label className="block space-y-1">
        <span className="text-sm opacity-80" style={theme?.text ? { color: theme.text } : undefined}>
            {label}
        </span>
        <input {...rest} data-testid={testid}
               className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3
                          text-base focus:border-white/40 focus:outline-none" />
    </label>
);
